import * as THREE from 'three';
import type { City } from '../world/city';
import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import type { Population, Person, TrafficVehicle } from './population';
import type { AirportLayout } from '../world/airport';
import { Reservations } from './reservations';

export interface GroundFlight {
  id: string;
  p: THREE.Vector3;
  state: string;
  stand: number;
  flights: number;
  test: boolean;
}
interface Unit {
  role: 'fuel' | 'baggage';
  car: TrafficVehicle;
  person: Person;
  depot: AirportLayout['groundDepot'][number];
  state:
    | 'ready'
    | 'collecting'
    | 'boarding'
    | 'entering'
    | 'exiting'
    | 'outbound'
    | 'inspection'
    | 'working'
    | 'crew-return'
    | 'returning'
    | 'unavailable';
  flight?: GroundFlight;
  cycle: number;
  timer: number;
  aboard: boolean;
  stock: number;
  point?: THREE.Vector3;
  park?: THREE.Vector3;
  reason: string;
  completed: number;
  returnTrip: boolean;
}
const vector = (p: Vec) => new THREE.Vector3(p.x, p.y, p.z);
/** Original crews and two shared vehicles perform real apron jobs. A clock
 * alone cannot release an aircraft: work and physical clearance must finish. */
export class AirportGround {
  units: Unit[] = [];
  private finished = new Map<string, number>();
  private jobs = 0;
  private leases: Reservations;
  private epoch = 0;
  private time = 0;
  log: { time: number; unit: string; flight?: string; cycle: number; action: string }[] = [];
  private line = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0x616e64 }),
  );
  private lineData = new Float32Array(6);
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly pop: Population,
    readonly flights: () => GroundFlight[],
  ) {
    this.leases = new Reservations(() => physics.life.epoch);
    this.line.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.lineData, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.line.geometry.setDrawRange(0, 0);
    this.line.frustumCulled = false;
    physics.scene.add(this.line);
  }
  private key(a: GroundFlight, role: Unit['role']): string {
    return `${a.id}:${role}`;
  }
  private serviced(a: GroundFlight, role: Unit['role']): boolean {
    return this.finished.get(this.key(a, role)) === a.flights;
  }
  private record(u: Unit, action: string): void {
    this.log.push({ time: this.time, unit: u.car.id, flight: u.flight?.id, cycle: u.cycle, action });
    if (this.log.length > 512) this.log.shift();
  }
  clear(): void {
    for (const u of this.units) {
      u.person.hidden = false;
      u.person.controlled = false;
      u.person.carry = '';
      if (u.car.state !== 'wreck') {
        u.car.plan = [];
        u.car.state = 'deployed';
      }
    }
    this.units = [];
    this.finished.clear();
    this.leases.clear();
    this.log = [];
    this.time = this.jobs = 0;
    this.line.geometry.setDrawRange(0, 0);
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    for (const depot of this.city.airport!.groundDepot) {
      const id = `airport-ground:${depot.role}`;
      const car =
        this.pop.cars.find((c) => c.id === id) ??
        this.pop.createCar(
          id,
          depot.role === 'fuel' ? 'airport-fuel' : 'airport-baggage',
          depot.home,
          this.city.airport!.serviceNode,
        );
      const person =
        this.pop.people.find((p) => p.id === `crew:${id}`) ?? this.pop.createCrew(`crew:${id}`, depot.supply);
      car.name = depot.role === 'fuel' ? 'Alder fuel and engineering vehicle' : 'Alder baggage carrier';
      car.controlled = true;
      car.state = 'deployed';
      car.speed = 3.2;
      car.plan = [];
      person.name = depot.role === 'fuel' ? 'Airport engineer and fuel operator' : 'Airport baggage handler';
      person.controlled = true;
      person.hidden = false;
      person.state = 'wait';
      person.layer = 'airport';
      this.units.push({
        role: depot.role,
        car,
        person,
        depot,
        state: 'ready',
        cycle: 0,
        timer: 0,
        aboard: false,
        stock: 24,
        reason: 'Available at airport service supply',
        completed: 0,
        returnTrip: false,
      });
    }
  }
  ready(a: GroundFlight): boolean {
    return (
      this.units.length === 2 &&
      this.units.every((u) => this.serviced(a, u.role)) &&
      !this.units.some((u) => u.flight === a)
    );
  }
  get laneBusy(): boolean {
    return this.units.some((u) => u.state === 'outbound' || u.state === 'returning');
  }
  private liveSupply(u: Unit): boolean {
    const owner = this.physics.cellOwner.get(u.depot.cell);
    return (
      owner !== undefined && !!(this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
    );
  }
  private walk(u: Unit, point: THREE.Vector3, dt: number): boolean {
    if (u.person.p.distanceTo(point) < 0.24) return true;
    u.person.state = 'walk';
    if (this.pop.footClear(u.person, point)) this.pop.walkTo(u.person, point, dt, 1.65);
    else this.pop.yieldFoot(u.person, point, dt);
    return false;
  }
  private door(u: Unit): THREE.Vector3 {
    return u.car.p.clone().add(new THREE.Vector3(1.6, 0, -1.3).applyQuaternion(u.car.q));
  }
  private cabin(u: Unit): THREE.Vector3 {
    return u.car.p.clone().add(new THREE.Vector3(0.3, 0.65, -1.3).applyQuaternion(u.car.q));
  }
  private flightMoving(): boolean {
    return this.flights().some(
      (a) => !a.test && ['pushback', 'taxi-out', 'takeoff', 'landing', 'taxi-in'].includes(a.state),
    );
  }
  private drive(u: Unit, returning: boolean): boolean {
    if (this.flightMoving() || !this.leases.acquire('apron-service-lane', u.car.id, this.time, 1, 120)) {
      u.reason = 'Waiting for the shared apron service lane to clear';
      return false;
    }
    const end = returning ? vector(u.depot.home) : u.park!;
    // The north service lane clears both parked wing envelopes and the hangar.
    const lane = -460 - (u.role === 'baggage' ? 8 : 0);
    const points = [
      new THREE.Vector3(u.car.p.x, u.car.p.y, lane),
      new THREE.Vector3(end.x, end.y, lane),
      end.clone(),
    ];
    u.car.plan = points.map((p) => ({ x: p.x, y: p.y, z: p.z, maxSpeed: 3.2 }));
    u.car.state = 'driving';
    u.car.think = 0;
    u.state = returning ? 'returning' : 'outbound';
    u.aboard = true;
    u.person.hidden = true;
    u.reason = returning
      ? 'Returning original ground crew and vehicle to supply'
      : 'Driving to the assigned aircraft stand';
    this.record(u, u.reason);
    return true;
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    for (const u of this.units) {
      if (u.state === 'unavailable') continue;
      u.person.previous.copy(u.person.p);
      if (u.aboard && u.car.state !== 'wreck') {
        u.person.p.copy(this.cabin(u));
        u.person.hidden = true;
      }
      if (
        u.car.state === 'wreck' ||
        ['incapacitated', 'falling', 'flee'].includes(u.person.state) ||
        !this.liveSupply(u)
      ) {
        u.state = 'unavailable';
        u.reason = 'Ground unit, crew or supply is damaged; aircraft remains unserviced';
        const injured = ['incapacitated', 'falling', 'flee'].includes(u.person.state);
        if (u.car.state === 'wreck' && u.aboard && !injured) this.pop.incapacitate(u.person, u.car.p, 2);
        u.person.controlled = !injured && u.car.state !== 'wreck';
        u.person.hidden = u.aboard && u.car.state !== 'wreck' && !injured;
        if (u.person.controlled) u.person.state = 'wait';
        u.aboard = u.person.hidden;
        if (u.car.state !== 'wreck') {
          u.car.plan = [];
          u.car.state = 'deployed';
        }
        this.leases.release(u.car.id);
        this.record(u, u.reason);
        continue;
      }
      if (
        u.flight &&
        (u.flight.state === 'wreck' || u.flight.flights !== u.cycle) &&
        !u.returnTrip &&
        u.state !== 'crew-return'
      ) {
        u.reason = 'Assigned aircraft unavailable; returning crew';
        if (u.aboard) {
          if (this.drive(u, true)) u.returnTrip = true;
        } else {
          u.state = 'crew-return';
          u.timer = 0;
        }
      }
      if (u.state === 'ready') {
        const a = this.flights().find(
          (a) => !a.test && a.state === 'turnaround' && !this.serviced(a, u.role),
        );
        if (!a || u.stock <= 0) {
          u.reason = u.stock <= 0 ? 'Service supply exhausted' : 'Waiting for aircraft turnaround';
          continue;
        }
        u.flight = a;
        u.cycle = a.flights;
        u.timer = 0;
        u.state = 'collecting';
        u.returnTrip = false;
        u.park = a.p.clone().add(new THREE.Vector3(23 + (u.role === 'baggage' ? 6 : 0), 0, -25));
        u.park.y = this.physics.ground(u.park, 0.3, 0.5)?.y ?? u.park.y;
        u.point = a.p.clone().add(new THREE.Vector3(3.4, 0, u.role === 'fuel' ? -11 : -7));
        u.point.y = this.physics.ground(u.point, 0.3, 0.5)?.y ?? u.point.y;
        this.record(u, 'Assigned original crew to aircraft turnaround');
      }
      if (u.state === 'collecting' && this.walk(u, vector(u.depot.supply), dt)) {
        u.person.state = 'collecting';
        u.reason = 'Collecting service equipment at the live supply cabinet';
        u.timer += dt;
        if (u.timer >= 6) {
          u.person.carry = u.role === 'fuel' ? 'service kit' : 'luggage';
          u.state = 'boarding';
          u.timer = 0;
        }
      }
      if ((u.state === 'boarding' || u.state === 'crew-return') && this.walk(u, this.door(u), dt)) {
        u.returnTrip = u.state === 'crew-return';
        u.state = 'entering';
        u.timer = 0;
      }
      if (u.state === 'entering') {
        u.timer = Math.min(1.2, u.timer + dt);
        u.person.state = 'boarding';
        u.person.previous.copy(u.person.p);
        u.person.p.lerpVectors(this.door(u), this.cabin(u), u.timer / 1.2);
        if (u.timer >= 1.2) this.drive(u, u.returnTrip);
      }
      if (u.state === 'outbound' || u.state === 'returning') {
        this.leases.acquire('apron-service-lane', u.car.id, this.time, 1, 120);
        if (u.car.state !== 'deployed') {
          if (u.car.blockedBy) u.reason = `Apron route waiting for ${u.car.blockedBy}`;
          continue;
        }
        this.leases.release(u.car.id);
        u.person.hidden = false;
        u.aboard = false;
        u.returnTrip = u.state === 'returning';
        u.state = 'exiting';
        u.timer = 0;
      }
      if (u.state === 'exiting') {
        u.timer = Math.min(1.2, u.timer + dt);
        u.person.state = 'alighting';
        u.person.previous.copy(u.person.p);
        u.person.p.lerpVectors(this.cabin(u), this.door(u), u.timer / 1.2);
        if (u.timer < 1.2) continue;
        u.person.state = 'walk';
        u.person.detour = undefined;
        if (u.returnTrip) {
          u.completed++;
          this.record(u, 'Ground crew and vehicle returned after service');
          u.flight = undefined;
          u.state = 'ready';
        } else {
          u.state = 'inspection';
          u.timer = 0;
          this.record(u, 'Parked clear of aircraft; original operator disembarked');
        }
      }
      if ((u.state === 'inspection' || u.state === 'working') && u.point && this.walk(u, u.point, dt)) {
        u.person.yaw = Math.atan2(u.person.p.x - u.flight!.p.x, u.person.p.z - u.flight!.p.z);
        u.person.state = u.role === 'fuel' ? 'working' : 'unloading';
        u.reason =
          u.state === 'inspection'
            ? 'Inspecting aircraft and safe servicing access'
            : u.role === 'fuel'
              ? 'Operator connected; transferring finite fuel allocation'
              : 'Loading baggage at the assigned aircraft';
        u.timer += dt;
        if (u.state === 'inspection' && u.timer >= 8) {
          u.state = 'working';
          u.timer = 0;
          this.record(u, 'Physical aircraft inspection completed');
        } else if (u.state === 'working' && u.timer >= (u.role === 'fuel' ? 16 : 12)) {
          u.stock--;
          this.jobs++;
          this.finished.set(this.key(u.flight!, u.role), u.cycle);
          u.person.carry = '';
          u.state = 'crew-return';
          this.record(
            u,
            u.role === 'fuel'
              ? 'Fuel transferred; hose disconnected'
              : 'Baggage transferred into assigned aircraft',
          );
        }
      }
    }
  }
  render(): void {
    const u = this.units.find((u) => u.role === 'fuel' && u.state === 'working' && u.flight);
    if (!u) {
      this.line.geometry.setDrawRange(0, 0);
      return;
    }
    this.lineData.set([
      u.person.p.x,
      u.person.p.y + 0.8,
      u.person.p.z,
      u.flight!.p.x + 5.5,
      u.flight!.p.y + 1.8,
      u.flight!.p.z - 1,
    ]);
    this.line.geometry.getAttribute('position').needsUpdate = true;
    this.line.geometry.setDrawRange(0, 2);
  }
  snapshot(): Record<string, number> {
    return {
      airportGroundUnits: this.units.length,
      airportGroundJobs: this.jobs,
      airportGroundReturns: this.units.reduce((n, u) => n + u.completed, 0),
    };
  }
  inspect(): unknown {
    return {
      ...this.snapshot(),
      units: this.units.map((u) => ({
        id: u.car.id,
        worker: u.person.id,
        role: u.role,
        state: u.state,
        reason: u.reason,
        aircraft: u.flight?.id,
        cycle: u.cycle,
        stock: u.stock,
        position: u.car.p.toArray(),
        workerPosition: u.person.p.toArray(),
        point: u.point?.toArray(),
        blocked: u.car.blockedBy,
      })),
      log: this.log,
    };
  }
  dispose(): void {
    this.clear();
    this.line.removeFromParent();
    this.line.geometry.dispose();
    this.line.material.dispose();
  }
}
